import { POSITION_FIELDS } from './constants';

type LoadOptions = (
  _inputValue: string
) => Promise<{ id: string; label: string }[]>;

interface BuildPositionFormConfigArgs {
  /** Per-reference-type loader from `usePositionReferenceOptions`. */
  loadOptionsFor: (_referenceType: string) => LoadOptions;
  /** True when that reference type came back with no options at all. */
  isEmptyList?: (_referenceType: string) => boolean;
  /**
   * The job profile matched from the Kula job. Seeded onto the field's own
   * option list — AsyncSelectField resolves a value's label from that list
   * alone, never from what `customLoadOptions` returns, so without it the
   * default is set but renders blank.
   */
  jobProfileDefault?: { id: string; name: string };
}

/**
 * Built on FormBuilder so the labels, spacing and field chrome match the HRIS
 * employee form. `isRequired` is all the validation needed — `constructYupSchema`
 * turns it into `"<Label> is required"`.
 */
export const buildPositionFormConfig = ({
  loadOptionsFor,
  isEmptyList = () => false,
  jobProfileDefault,
}: BuildPositionFormConfigArgs) =>
  POSITION_FIELDS.filter(
    // A tenant that does not use Company or Cost Centre gets no field at all;
    // FormBuilder clears the value of an unmounted field, so the key is then
    // omitted from the request too.
    (field) =>
      !(
        field.hideWhenEmpty &&
        field.referenceType &&
        isEmptyList(field.referenceType)
      )
  ).map((field) => {
    if (!field.referenceType) {
      return {
        id: field.name,
        name: field.name,
        label: field.label,
        type: 'input',
        isRequired: field.required,
        placeholder: field.label,
      };
    }

    return {
      id: field.name,
      name: field.name,
      label: field.label,
      type: 'asyncSelect',
      isRequired: field.required,
      placeholder: `Search ${field.label.toLowerCase()}`,
      valueKey: 'id',
      labelKey: 'label',
      loadDefaultOptions: true,
      isClearable: true,
      customLoadOptions: loadOptionsFor(field.referenceType),
      ...(field.name === 'job_profile_id' && jobProfileDefault
        ? {
            options: [
              { id: jobProfileDefault.id, label: jobProfileDefault.name },
            ],
          }
        : {}),
    };
  });
